import { create } from "zustand";
import { generateUUID } from "../utils/uuid";
import { DEFAULT_ROOM_CONFIG, DEFAULT_MODEL_PROPS } from "../constants/defaults";

// Session-specific storage that isolates room data per session ID
const createSessionKey = (sessionId, key) => `room-editor-${sessionId}-${key}`;

// Get session-specific data from localStorage
const getSessionData = (sessionId, key, defaultValue) => {
  try {
    const stored = localStorage.getItem(createSessionKey(sessionId, key));
    return stored ? JSON.parse(stored) : defaultValue;
  } catch (error) {
    console.warn(`Failed to parse session data for ${sessionId}:${key}`, error);
    return defaultValue;
  }
};

// Store session-specific data to localStorage
const setSessionData = (sessionId, key, data) => {
  try {
    localStorage.setItem(createSessionKey(sessionId, key), JSON.stringify(data));
    return true;
  } catch (error) {
    console.error(`Failed to store session data for ${sessionId}:${key}`, error);
    return false;
  }
};

// Create session-specific room store
export const createRoomStore = (sessionId) => create((set, get) => {
  // Load existing data for this session
  const existingData = getSessionData(sessionId, 'room', {
    scaleX: DEFAULT_ROOM_CONFIG.scaleX,
    scaleZ: DEFAULT_ROOM_CONFIG.scaleZ,
    height: DEFAULT_ROOM_CONFIG.height,
    materialType: DEFAULT_ROOM_CONFIG.materialType,
    materialId: DEFAULT_ROOM_CONFIG.materialId,
    materialOptions: {},
    decorations: []
  });

  return {
    ...existingData,

    setScaleX: (val) => {
      set({ scaleX: val });
      setSessionData(sessionId, 'room', { ...get(), scaleX: val });
    },

    setScaleZ: (val) => {
      set({ scaleZ: val });
      setSessionData(sessionId, 'room', { ...get(), scaleZ: val });
    },

    setHeight: (val) => {
      set({ height: val });
      setSessionData(sessionId, 'room', { ...get(), height: val });
    },

    setMaterialType: (type) => {
      set({ materialType: type });
      setSessionData(sessionId, 'room', { ...get(), materialType: type });
    },

    setMaterialId: (id) => {
      set({ materialId: id });
      setSessionData(sessionId, 'room', { ...get(), materialId: id });
    },

    setMaterialOptions: (materialOptions) => {
      set({ materialOptions });
      setSessionData(sessionId, 'room', { ...get(), materialOptions });
    },

    setDecorations: (decorations) => {
      set({ decorations });
      setSessionData(sessionId, 'room', { ...get(), decorations });
    },

    addDecoration: (decoration) => {
      const newDecorations = [...get().decorations, decoration];
      set({ decorations: newDecorations });
      setSessionData(sessionId, 'room', { ...get(), decorations: newDecorations });
    },

    removeDecoration: (idx) => {
      const newDecorations = get().decorations.filter((_, i) => i !== idx);
      set({ decorations: newDecorations });
      setSessionData(sessionId, 'room', { ...get(), decorations: newDecorations });
    },

    editDecoration: (index, newProps) => {
      const updated = [...get().decorations];
      updated[index] = { ...updated[index], ...newProps };
      set({ decorations: updated });
      setSessionData(sessionId, 'room', { ...get(), decorations: updated });
    },

    // Load room data from external source
    loadRoomData: (roomData) => {
      const newState = {
        scaleX: roomData.scaleX || DEFAULT_ROOM_CONFIG.scaleX,
        scaleZ: roomData.scaleZ || DEFAULT_ROOM_CONFIG.scaleZ,
        height: roomData.height || DEFAULT_ROOM_CONFIG.height,
        materialType: roomData.materialType || DEFAULT_ROOM_CONFIG.materialType,
        materialId: roomData.materialId || DEFAULT_ROOM_CONFIG.materialId,
        materialOptions: roomData.materialOptions || {},
        decorations: roomData.decorations || []
      };
      set(newState);
      setSessionData(sessionId, 'room', newState);
    },

    // Clear session data
    clearSession: () => {
      localStorage.removeItem(createSessionKey(sessionId, 'room'));
      localStorage.removeItem(createSessionKey(sessionId, 'models'));
    }
  };
});

// Create session-specific placed models store
export const createPlacedModelsStore = (sessionId) => create((set, get) => {
  // Load existing data for this session
  const existingModels = getSessionData(sessionId, 'models', []);

  const saveToStorage = () => {
    setSessionData(sessionId, 'models', get().placedModels);
  };

  return {
    placedModels: existingModels,

    getModelById: (id) => {
      return get().placedModels.find((model) => model.id === id);
    },

    addModel: ({
      url,
      name,
      position = [0, 0, 0],
      rotation = [0, 0, 0],
      scale = [1, 1, 1],
      hue,
      assetId = null,
      category = null,
    }) => {
      set((state) => ({
        placedModels: [
          ...state.placedModels,
          {
            id: generateUUID(),
            name,
            url,
            position,
            rotation,
            scale,
            hue,
            assetId,
            category,
            music: DEFAULT_MODEL_PROPS.music,
            link: DEFAULT_MODEL_PROPS.link,
            hoverText: DEFAULT_MODEL_PROPS.hoverText,
          },
        ],
      }));
      saveToStorage();
    },

    updateTransform: (id, transform) => {
      set((state) => ({
        placedModels: state.placedModels.map((model) =>
          model.id === id ? { ...model, ...transform } : model
        ),
      }));
      saveToStorage();
    },

    updateName: (id, newName) => {
      set((state) => ({
        placedModels: state.placedModels.map((model) =>
          model.id === id ? { ...model, name: newName } : model
        ),
      }));
      saveToStorage();
    },

    updateMusic: (id, newMusic) => {
      set((state) => ({
        placedModels: state.placedModels.map((model) =>
          model.id === id ? { ...model, music: newMusic } : model
        ),
      }));
      saveToStorage();
    },

    updateLink: (id, newLink) => {
      set((state) => ({
        placedModels: state.placedModels.map((model) =>
          model.id === id ? { ...model, link: newLink } : model
        ),
      }));
      saveToStorage();
    },

    updateHoverText: (id, newHoverText) => {
      set((state) => ({
        placedModels: state.placedModels.map((model) =>
          model.id === id ? { ...model, hoverText: newHoverText } : model
        ),
      }));
      saveToStorage();
    },

    updateHue: (id, hue) => {
      set((state) => ({
        placedModels: state.placedModels.map((model) =>
          model.id === id ? { ...model, hue: hue } : model
        ),
      }));
      saveToStorage();
    },

    removeModel: (id) => {
      set((state) => ({
        placedModels: state.placedModels.filter((model) => model.id !== id),
      }));
      saveToStorage();
    },

    clearAll: () => {
      set({ placedModels: [] });
      saveToStorage();
    },

    // Load models from external source
    loadModels: (models) => {
      set({ placedModels: models });
      saveToStorage();
    },

    // Replace all models
    replaceModels: (models) => {
      set({ placedModels: models });
      saveToStorage();
    }
  };
});

// Session store registry to track active session stores
const sessionStoreRegistry = new Map();

// Get or create session-specific stores
export const getSessionStores = (sessionId) => {
  if (!sessionId) {
    throw new Error('Session ID is required for session stores');
  }

  if (!sessionStoreRegistry.has(sessionId)) {
    sessionStoreRegistry.set(sessionId, {
      roomStore: createRoomStore(sessionId),
      placedModelsStore: createPlacedModelsStore(sessionId)
    });
  }

  return sessionStoreRegistry.get(sessionId);
};

// Clean up session stores (call when session ends)
export const cleanupSession = (sessionId) => {
  const stores = sessionStoreRegistry.get(sessionId);
  if (stores) {
    stores.roomStore.getState().clearSession();
    sessionStoreRegistry.delete(sessionId);
  }
};
